import { memo, useCallback, useEffect, FC, useRef, useState } from "react";
import {
    Button,
    Center,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Spacer,
    Spinner,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Th,
    Tr,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Box,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Text,
    UnorderedList,
    ListItem,
} from "@chakra-ui/react";

import { useStepFunctions } from "../../../hooks/useStepFunctins";
import { PageDiscription } from "../../atoms/button/PageDiscription";
import { TableContent } from "../../organisms/layout/TableContent";
import { useLoginUser } from "../../../hooks/useLoginUser";
import { RepeatIcon, WarningIcon } from "@chakra-ui/icons";
import { SfnExecutionRow } from "../../organisms/datarow/SfnExecutionRow";
import { useParameters } from "../../../hooks/useParameters";
import appconfig from "../../../config.json";
import { TenantSingleParm } from "../../../types/api/parameterTypes";

export const SensorConfigDeletion: FC = memo(() => {
    const { loginUser, selectTenant } = useLoginUser();
    const { getParameter, parameter, loading } = useParameters();
    const { startExecution, getListExecutions, loading: sfnExecutionListLoading, sfnExecutions } = useStepFunctions();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isConfirmed, setIsConfirmed] = useState(false);

    const didLogRef = useRef(false);
    useEffect(() => {
        if (didLogRef.current === false) {
            didLogRef.current = true;
            let tenant_id: string = "";
            if (selectTenant) {
                tenant_id = selectTenant;
            } else {
                if (loginUser) tenant_id = loginUser.tenant;
            }
            const basePath = "/" +
                appconfig.IOT_MANAGEMENT_ENV +
                "/" +
                tenant_id +
                "/";

            // センサー設定削除用のStateMachine ARNを取得（削除モード）
            const parameterPath = basePath + TenantSingleParm.DevicesDestroyArn;
            console.log("センサー設定削除用パラメータパス:", parameterPath);
            getParameter(parameterPath);
        }
    }, []);

    useEffect(() => {
        if (parameter) {
            console.log("センサー設定削除用パラメータ取得完了:", parameter);
            getListExecutions(parameter);
        }
    }, [parameter]);

    // 削除開始ボタンクリック時の処理
    const onClickDeleteStart = useCallback(() => {
        // AccountAdminチェック
        if (!loginUser?.isAccountAdmin) {
            alert("この操作はAccountAdminのみ実行可能です。");
            return;
        }
        
        // 確認モーダル表示
        onOpen();
    }, [loginUser, onOpen]);

    // 削除実行の最終確認
    const onConfirmDelete = useCallback(async () => {
        if (parameter && isConfirmed) {
            try {
                console.log("センサー設定削除実行開始:", parameter);
                const result = await startExecution(parameter);
                console.log("センサー設定削除実行結果:", result);
                onClose();
                setIsConfirmed(false);
                // 実行後に状態を更新
                setTimeout(() => {
                    getListExecutions(parameter);
                }, 1000);
            } catch (error) {
                console.error("センサー設定削除実行エラー:", error);
                alert(`削除実行に失敗しました: ${error}`);
            }
        } else {
            console.warn("削除実行条件が満たされていません:", { parameter, isConfirmed });
        }
    }, [parameter, isConfirmed, startExecution, onClose, getListExecutions]);

    // 更新ボタン（StepFunctionsの処理状態を取得しなおす）
    const onClickReload = useCallback(() => {
        if (parameter) {
            getListExecutions(parameter);
        }
    }, [parameter, getListExecutions]);

    // モーダルを閉じる際の処理
    const handleModalClose = useCallback(() => {
        setIsConfirmed(false);
        onClose();
    }, [onClose]);

    return (
        <Stack maxW="1200px">
            <PageDiscription>
                センサー設定削除の実行状態です。
                <br />
                <strong>⚠️ 重要: この処理はテナント削除前にのみ実行してください。</strong>
            </PageDiscription>

            {/* 重要な警告メッセージ */}
            <Alert status="warning" mb={4}>
                <AlertIcon />
                <Box>
                    <AlertTitle>重要な注意事項</AlertTitle>
                    <AlertDescription>
                        <UnorderedList mt={2}>
                            <ListItem>この処理は<strong>テナント削除前の必須処理</strong>としてのみ実行してください</ListItem>
                            <ListItem><strong>IoTDeviceCDKのAWSリソース</strong>（Lambda、API Gateway等）が削除されます</ListItem>
                            <ListItem>設定・定義・レシーブAPIは保持されるため、<strong>Deploy用StateMachine再実行で復旧可能</strong>です</ListItem>
                            <ListItem>AccountAdminのみが実行可能です</ListItem>
                        </UnorderedList>
                    </AlertDescription>
                </Box>
            </Alert>

            <Tabs>
                <TabList>
                    <Tab>{parameter}</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel>
                        <HStack my="2" h="10" alignItems={"flex-end"}>
                            <FormLabel>センサー設定削除実行状態</FormLabel>
                            <Spacer />
                            <Flex pr="5">
                                <Button
                                    colorScheme="red"
                                    variant="solid"
                                    onClick={onClickDeleteStart}
                                    size="sm"
                                    leftIcon={<WarningIcon />}
                                    isDisabled={!loginUser?.isAccountAdmin}
                                >
                                    IoTDeviceCDK削除
                                </Button>
                            </Flex>
                            <Flex pr="3">
                                <Button
                                    leftIcon={<RepeatIcon />}
                                    colorScheme="teal"
                                    variant="solid"
                                    onClick={onClickReload}
                                    size="sm"
                                >
                                    {"状態更新"}
                                </Button>
                            </Flex>
                        </HStack>
                        {sfnExecutionListLoading ? (
                            <Center h="50vh">
                                <Spinner color="teal.500" />
                            </Center>
                        ) : (
                            <>
                                {sfnExecutions.length > 0 ? (
                                    <TableContent
                                        heightOffset="-100px"
                                        headerRows={
                                            <Tr key="header">
                                                <Th>削除開始日時</Th>
                                                <Th>削除完了日時</Th>
                                                <Th>状態</Th>
                                                <Th w="100%">実行No</Th>
                                            </Tr>
                                        }
                                        bodyRows={[
                                            <SfnExecutionRow
                                                key={sfnExecutions[0].executionArn}
                                                ExecutionArn={sfnExecutions[0].executionArn}
                                                Name={sfnExecutions[0].name}
                                                StartDate={sfnExecutions[0].startDate}
                                                Status={sfnExecutions[0].status}
                                                StopDate={sfnExecutions[0].stopDate}
                                            />
                                        ]}
                                    ></TableContent>
                                ) : (
                                    <Box p={4} textAlign="center" color="gray.500">
                                        センサー設定削除は実行されていません
                                    </Box>
                                )}
                            </>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>

            {/* 削除確認モーダル */}
            <Modal isOpen={isOpen} onClose={handleModalClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color="red.500">
                        <WarningIcon mr={2} />
                        センサー設定削除の最終確認
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status="warning" mb={4}>
                            <AlertIcon />
                            <AlertTitle>⚠️ AWSリソース削除の確認</AlertTitle>
                        </Alert>
                        
                        <Text mb={4} fontWeight="bold">
                            以下のAWSリソースが削除されます：
                        </Text>
                        
                        <UnorderedList mb={4} spacing={1}>
                            <ListItem>IoTDeviceCDKデプロイリソース（Lambda、API Gateway、IAMロール等）</ListItem>
                            <ListItem>関連するCloudFormationスタック</ListItem>
                        </UnorderedList>

                        <Alert status="info" mb={4}>
                            <AlertIcon />
                            <AlertDescription>
                                <strong>削除されない要素（保持されます）：</strong>
                                <br />
                                • レシーブAPI（データ受信用API）
                                <br />
                                • API定義JSON設定
                                <br />
                                • エンティティタイプ定義・設定
                                <br />
                                • ドライバー設定・マッピング情報
                                <br />
                                • 履歴・ログ情報
                            </AlertDescription>
                        </Alert>

                        <Alert status="warning" mb={4}>
                            <AlertIcon />
                            <AlertDescription>
                                <strong>この処理はテナント削除前にのみ実行してください。</strong>
                                <br />
                                AWSリソース削除後も設定は保持されるため、Deploy用StateMachine再実行で復旧可能です。
                            </AlertDescription>
                        </Alert>

                        <Box bg="gray.50" p={4} borderRadius="md" mb={4}>
                            <Text fontWeight="bold" mb={2}>削除処理の内容：</Text>
                            <Text fontSize="sm">
                                IoTDeviceCDKリソース削除のみ実行
                                <br />
                                <em>※ 設定・定義・レシーブAPIは削除されません</em>
                            </Text>
                        </Box>

                        <FormControl>
                            <Box>
                                <input
                                    type="checkbox"
                                    id="deleteConfirm"
                                    checked={isConfirmed}
                                    onChange={(e) => setIsConfirmed(e.target.checked)}
                                />
                                <label htmlFor="deleteConfirm" style={{ marginLeft: '8px' }}>
                                    <strong>上記の内容を理解し、IoTDeviceCDKリソース削除を実行することを確認しました</strong>
                                </label>
                            </Box>
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={handleModalClose}>
                            キャンセル
                        </Button>
                        <Button
                            colorScheme="red"
                            onClick={onConfirmDelete}
                            isDisabled={!isConfirmed}
                            leftIcon={<WarningIcon />}
                        >
                            IoTDeviceCDK削除実行
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Stack>
    );
});
